import { FormEvent, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useCalls } from "@/lib/calls";
import { useSession } from "@/lib/session";
import { localNumberInput, normalizeLocalNumber } from "@/lib/shared";

type CallRow = {
  id: number;
  type?: string;
  extension?: string;
  caller_id_number?: string;
  status?: string;
  created_at?: string;
  uuid?: string;
};

const KEYS: Array<{ digit: string; letters?: string }> = [
  { digit: "1" },
  { digit: "2", letters: "ABC" },
  { digit: "3", letters: "DEF" },
  { digit: "4", letters: "GHI" },
  { digit: "5", letters: "JKL" },
  { digit: "6", letters: "MNO" },
  { digit: "7", letters: "PQRS" },
  { digit: "8", letters: "TUV" },
  { digit: "9", letters: "WXYZ" },
  { digit: "0", letters: "+" },
];

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function formatDial(value: string) {
  const d = value.replace(/\D/g, "").slice(0, 10);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)} ${d.slice(5)}`;
}

export default function DashboardPage() {
  const { user } = useSession();
  const { clickToCall, live, myCall, hangup, lineStatus, freeLine } = useCalls();
  const [recent, setRecent] = useState<CallRow[]>([]);
  const [mqttOk, setMqttOk] = useState<boolean | null>(null);
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    const [calls, health] = await Promise.all([
      api<CallRow[]>("/calls").catch(() => [] as CallRow[]),
      api<{ connected?: boolean }>("/pbx/mqtt/health").catch(() => null),
    ]);
    setRecent(Array.isArray(calls) ? calls.filter((c) => c.type !== "hangup").slice(0, 8) : []);
    setMqttOk(health ? Boolean(health.connected) : null);
  }

  useEffect(() => {
    void load().catch(() => undefined);
    const t = window.setInterval(() => void load().catch(() => undefined), 8000);
    return () => window.clearInterval(t);
  }, []);

  // Keyboard + numpad digits on the dashboard
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === "TEXTAREA" || el.tagName === "SELECT")) return;

      const dialInput = document.getElementById("dash-dial-input");
      if (el?.tagName === "INPUT" && el !== dialInput) return;

      const fromNumpad = e.code.startsWith("Numpad") && e.code.length === 7;
      const digit =
        e.key >= "0" && e.key <= "9"
          ? e.key
          : fromNumpad && e.code.slice(-1) >= "0" && e.code.slice(-1) <= "9"
            ? e.code.slice(-1)
            : "";

      if (digit) {
        e.preventDefault();
        setPhone((p) => localNumberInput(p + digit));
        return;
      }
      if (e.key === "Backspace") {
        e.preventDefault();
        setPhone((p) => p.slice(0, -1));
        return;
      }
      if (e.key === "Enter" || e.code === "NumpadEnter") {
        const d = localNumberInput(phone);
        if (d.length === 10 && !busy && !myCall) {
          e.preventDefault();
          void placeCall();
        }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phone, busy, myCall]);

  const stats = useMemo(() => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const today = recent.filter((c) => new Date(c.created_at || 0).getTime() >= todayStart.getTime());
    const ok = today.filter((c) =>
      ["answered", "completed", "hungup"].includes(String(c.status || "").toLowerCase()),
    );
    return [
      { label: "Line", value: lineStatus || "idle", hint: `Ext ${user?.extension || "—"}` },
      { label: "Live", value: String(live.length), hint: myCall ? myCall.to : "No active call" },
      { label: "Today", value: String(today.length), hint: `${ok.length} connected` },
      {
        label: "PBX link",
        value: mqttOk == null ? "…" : mqttOk ? "Online" : "Offline",
        hint: "MQTT broker",
      },
    ];
  }, [recent, live, myCall, lineStatus, user?.extension, mqttOk]);

  async function placeCall() {
    const ten = localNumberInput(phone);
    if (ten.length !== 10) {
      setError("Enter exactly 10 digits.");
      return;
    }
    const dialNumber = normalizeLocalNumber(ten); // auto 0 + 10 digits
    setError("");
    setStatus(`Dialing ${dialNumber}…`);
    setBusy(true);
    try {
      await clickToCall(dialNumber);
      setStatus(`Calling ${dialNumber}`);
      await load();
    } catch (err) {
      setStatus("");
      setError(err instanceof Error ? err.message : "Call failed");
    } finally {
      setBusy(false);
    }
  }

  async function dial(e?: FormEvent) {
    e?.preventDefault();
    await placeCall();
  }

  function pad(digit: string) {
    if (!/^\d$/.test(digit)) return;
    setPhone((p) => localNumberInput(p + digit));
  }

  async function redial(raw: string) {
    const ten = localNumberInput(raw);
    if (ten.length !== 10) return;
    setPhone(ten);
    setError("");
    setStatus("");
    setBusy(true);
    try {
      const dialNumber = normalizeLocalNumber(ten);
      setStatus(`Dialing ${dialNumber}…`);
      await clickToCall(dialNumber);
      setStatus(`Calling ${dialNumber}`);
      await load();
    } catch (err) {
      setStatus("");
      setError(err instanceof Error ? err.message : "Call failed");
    } finally {
      setBusy(false);
    }
  }

  const digits = localNumberInput(phone);
  const inCall = Boolean(myCall);

  return (
    <>
      <div className="top top-compact">
        <h1 className="top-oneline">
          <span className="muted">{greeting()}, {user?.name?.split(" ")[0] || "there"}</span>
          <span className="top-sep">·</span>
          Call dashboard
          <span className="top-sep">·</span>
          <span className="muted">
            Ext <b>{user?.extension || "—"}</b> auto-answer
          </span>
        </h1>
        <div className="top-actions">
          {(lineStatus === "inuse" || lineStatus === "busy" || lineStatus === "ringing" || inCall) && (
            <button
              className="btn ghost"
              type="button"
              onClick={() => void (myCall ? hangup(myCall.uuid) : freeLine())}
            >
              Free line
            </button>
          )}
        </div>
      </div>

      <div className="stat-grid">
        {stats.map((s) => (
          <div className="stat" key={s.label}>
            <span className="stat-label">{s.label}</span>
            <strong className="stat-value">{s.value}</strong>
            <span className="muted">{s.hint}</span>
          </div>
        ))}
      </div>

      <div className="dash-grid" style={{ display: "grid", gridTemplateColumns: "minmax(0, 420px) 1fr", gap: 16 }}>
        <section className="panel dialer-card">
          <div className="panel-head">
            <h3>Quick dial</h3>
            <span className={`pill ${inCall ? "live" : "idle"}`}>
              {inCall ? myCall?.state : "Ready"}
            </span>
          </div>

          <form onSubmit={(e) => void dial(e)}>
            <div className="dial-display">
              <input
                id="dash-dial-input"
                aria-label="Number to dial"
                placeholder="10-digit mobile"
                value={formatDial(phone)}
                maxLength={12}
                inputMode="numeric"
                autoComplete="off"
                onChange={(e) => setPhone(localNumberInput(e.target.value))}
              />
              <button
                className="dial-back"
                type="button"
                onClick={() => setPhone((p) => p.slice(0, -1))}
                disabled={!phone}
                aria-label="Backspace"
              >
                ⌫
              </button>
            </div>

            <div className="dial-pad">
              {KEYS.map((k) => (
                <button
                  key={k.digit}
                  type="button"
                  className={k.digit === "0" ? "dial-zero" : undefined}
                  onClick={() => pad(k.digit)}
                  disabled={busy || inCall}
                >
                  <b>{k.digit}</b>
                  {k.letters ? <span>{k.letters}</span> : null}
                </button>
              ))}
            </div>

            <div className="dial-feedback">
              {error ? (
                <p className="error">{error}</p>
              ) : status ? (
                <p className="ok-msg">{status}</p>
              ) : digits.length === 10 ? (
                <p className="muted dial-hint">
                  Will dial <b>0{digits}</b>
                </p>
              ) : (
                <p className="muted dial-hint">Enter 10 digits · 0 added on Call</p>
              )}
            </div>

            <div className="dial-actions dial-actions-3">
              <button
                className="btn mint"
                type="submit"
                disabled={busy || digits.length !== 10 || inCall}
              >
                {busy ? "Calling…" : "Call"}
              </button>
              <button
                className="btn ghost"
                type="button"
                disabled={busy || !inCall}
                onClick={() => myCall && void hangup(myCall.uuid)}
              >
                End
              </button>
              <button
                className="btn ghost"
                type="button"
                onClick={() => {
                  setPhone("");
                  setError("");
                  setStatus("");
                }}
              >
                Clear
              </button>
            </div>
          </form>

          {myCall && (
            <p className="muted active-call-line">
              Active: Ext {myCall.from} → {myCall.to} ({myCall.state})
            </p>
          )}
        </section>

        <section className="panel recent-panel">
          <div className="panel-head">
            <h3>Recent</h3>
            <span className="muted" style={{ fontSize: 12 }}>
              Tap Call to redial
            </span>
          </div>
          <ul className="recent-call-list">
            {recent.length === 0 && <li className="muted empty-recent">No calls yet</li>}
            {recent.map((c) => {
              const ten = String(c.caller_id_number || "")
                .replace(/\D/g, "")
                .replace(/^0/, "")
                .slice(-10);
              const label =
                c.status === "answered" || c.status === "completed"
                  ? "Connected"
                  : c.status === "success"
                    ? "Dialed"
                    : c.status === "hungup"
                    ? "Ended"
                    : c.status === "failed" || c.status === "timed_out"
                      ? "Failed"
                      : c.status || "—";
              return (
                <li key={c.id} className="recent-call-row">
                  <div className="recent-call-meta">
                    <b>{ten ? formatDial(ten) : c.caller_id_number || "—"}</b>
                    <span className="muted">
                      Ext {c.extension || "—"} · {label}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn mint recent-call-btn"
                    disabled={busy || inCall || ten.length !== 10}
                    onClick={() => void redial(ten)}
                  >
                    Call
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      </div>
    </>
  );
}
